import React, { useState } from 'react';
import { Award, Printer, X } from 'lucide-react';
import type { TrainingProgress } from '@/data/trainingTypes';

/**
 * Printable Social Media Hub Certification certificate.
 * Uses a print-scoped stylesheet so window.print() outputs ONLY the certificate.
 */
const Certificate: React.FC<{
  progress: TrainingProgress;
  onClose: () => void;
  onNameChange: (name: string) => void;
}> = ({ progress, onClose, onNameChange }) => {
  const [name, setName] = useState(progress.certifiedName || '');
  const best = progress.attempts.reduce((m, a) => Math.max(m, a.scorePct), 0);
  const date = progress.certifiedAt
    ? new Date(progress.certifiedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const certId = `ZT-SMH-${(progress.certifiedAt || new Date().toISOString()).slice(0, 10).replace(/-/g, '')}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm">
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #zera-cert, #zera-cert * { visibility: visible !important; }
          #zera-cert {
            position: fixed !important; inset: 0 !important; margin: 0 !important;
            width: 100% !important; height: 100% !important; border-radius: 0 !important;
            display: flex !important; align-items: center !important; justify-content: center !important;
            background: #0a0a0a !important; -webkit-print-color-adjust: exact; print-color-adjust: exact;
          }
          .cert-no-print { display: none !important; }
        }
      `}</style>

      <div className="w-full max-w-3xl">
        <div className="cert-no-print mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <input
            value={name}
            onChange={(e) => { setName(e.target.value); onNameChange(e.target.value); }}
            placeholder="Type the recipient's name…"
            className="rounded-full border border-white/20 bg-white/[0.06] px-5 py-2.5 text-sm text-white placeholder:text-white/40 focus:border-[#C9A961]/60 focus:outline-none sm:w-72"
          />
          <div className="flex gap-2">
            <button
              onClick={() => window.print()}
              className="flex items-center gap-2 rounded-full bg-[#C9A961] px-5 py-2.5 text-xs font-semibold text-black hover:bg-[#E4C77F]"
            >
              <Printer className="h-4 w-4" /> Print / Save PDF
            </button>
            <button
              onClick={onClose}
              className="flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-xs text-white/70 hover:text-white"
            >
              <X className="h-4 w-4" /> Close
            </button>
          </div>
        </div>

        {/* The certificate itself */}
        <div id="zera-cert" className="overflow-hidden rounded-3xl bg-[#0a0a0a] p-1">
          <div className="rounded-[22px] border-2 border-[#C9A961]/60 p-8 text-center sm:p-12" style={{ background: 'radial-gradient(ellipse at top, rgba(201,169,97,0.12), transparent 60%), #0a0a0a' }}>
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full border border-[#C9A961]/60 bg-[#C9A961]/15">
              <Award className="h-8 w-8 text-[#E4C77F]" />
            </div>
            <div className="text-[10px] font-medium uppercase tracking-[0.5em] text-[#C9A961]">Zera Travel · Admin Academy</div>
            <h1 className="mt-4 text-3xl font-light tracking-tight text-white sm:text-4xl">Certificate of Completion</h1>
            <p className="mt-6 text-xs uppercase tracking-[0.3em] text-white/40">This certifies that</p>
            <div className="mx-auto mt-3 max-w-md border-b border-[#C9A961]/40 pb-2 text-2xl font-medium text-[#E4C77F] sm:text-3xl">
              {name || '________________'}
            </div>
            <p className="mx-auto mt-6 max-w-lg text-sm leading-relaxed text-white/60">
              has successfully completed the <span className="text-white">Social Media Hub Certification</span> —
              demonstrating proficiency in brand voice, template-driven content creation, AI media generation,
              approval workflows, scheduling, publishing and analytics.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-6 sm:flex-row sm:gap-14">
              <div>
                <div className="text-lg font-semibold text-white">{best}%</div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Best score</div>
              </div>
              <div>
                <div className="text-lg font-semibold text-white">{date}</div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Date certified</div>
              </div>
              <div>
                <div className="text-lg font-semibold text-white">{certId}</div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Certificate ID</div>
              </div>
            </div>
            <div className="mt-10 text-[10px] uppercase tracking-[0.35em] text-white/30">zeratravel.com · MTN Social Academy</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Certificate;
